import { useCallback, useMemo } from "react";

import { AnyObject } from "@hen/stdlib/types";
import { UseMutationResult } from "@tanstack/react-query";

import { getModelsReferencedInOpTransactions } from "lib/shared/object-pool";
import { ObjectPoolManifestContext } from "lib/shared/object-pool/manifest";
import {
  mergeEntityMapManifestInQueryCache,
  resolveObjectPoolTransactionsInQueryCache,
  useBoundQueryClient,
} from "store/objectPool/queryCache";
import { ObjectPoolEntityMapManifest, ObjectPoolModelName } from "utils/types";

import { getInvalidateQueriesDependentOnModels } from "./prismaObjectSync/getInvalidateQueries";
import { getShouldInvalidateBroadQuery } from "./prismaObjectSync/getShouldInvalidateBroadQuery";
import { useCompanyObject } from "../../services/company/useCompanyObject";
import { useMeasureDuration } from "../../utils/measureInteraction";
import { trpcCmVanilla } from "../../utils/trpc";

export function useResolveObjectPoolTransactions() {
  const boundQueryClient = useBoundQueryClient();
  const measureDuration = useMeasureDuration("resolve_object_pool_transactions");
  const resolveObjectPoolTransactions = useCallback(
    // @ts-expect-error TS7006
    (transactions, { skipInvalidateQueries = false }: { skipInvalidateQueries?: boolean } = {}) => {
      measureDuration.start();
      resolveObjectPoolTransactionsInQueryCache(boundQueryClient, transactions);

      if (!skipInvalidateQueries) {
        const { queryClient } = boundQueryClient;
        setTimeout(() => {
          const models = getModelsReferencedInOpTransactions(transactions);

          const invalidateQueriesDependentOnModels = getInvalidateQueriesDependentOnModels(
            queryClient,
            models,
          );
          invalidateQueriesDependentOnModels();
        }, 10);
      }

      measureDuration.complete();
    },
    [boundQueryClient, measureDuration],
  );
  return resolveObjectPoolTransactions;
}

function throwOnAttemptMutate() {
  throw new Error(
    "useTransactionalMutation should not be used with mutate - use mutateAsync instead",
  );
}

export function useTransactionalMutation<TMutation extends UseMutationResult>(
  trpcMutation: TMutation,
) {
  const resolveObjectPoolTransactions = useResolveObjectPoolTransactions();
  const mutateAsync = useCallback(
    // @ts-expect-error TS7006
    async (mutationArgs) => {
      // @ts-expect-error TS2339
      const { transactions, meta } = await trpcMutation.mutateAsync(mutationArgs);
      resolveObjectPoolTransactions(transactions);
      return meta;
    },
    [resolveObjectPoolTransactions, trpcMutation],
  );
  const result = useMemo(
    () => ({
      ...trpcMutation,
      mutate: throwOnAttemptMutate,
      mutateAsync,
    }),
    [mutateAsync, trpcMutation],
  );
  return result;
}

export function useObjectPoolInvalidation() {
  const boundQueryClient = useBoundQueryClient();
  const resolveObjectPoolTransactions = useResolveObjectPoolTransactions();

  const invalidateAsync = useCallback(
    async (models: Array<ObjectPoolModelName>, contextArg?: ObjectPoolManifestContext) => {
      const context = contextArg ?? { type: "global" };
      const { companyId } = boundQueryClient;
      // @ts-expect-error TS2322
      const result: Partial<ObjectPoolEntityMapManifest> =
        await trpcCmVanilla.object.manifest.findAll.query({
          where: { companyId },
          models,
          context,
        });
      const shouldInvalidateBroadQuery = getShouldInvalidateBroadQuery(models);
      mergeEntityMapManifestInQueryCache(boundQueryClient, result);
      const { queryClient } = boundQueryClient;
      await queryClient.invalidateQueries({
        predicate: (query) => shouldInvalidateBroadQuery(query.queryKey),
      });
    },
    [boundQueryClient],
  );

  const updateAsync = useCallback(
    async <T extends Array<ObjectPoolModelName>>(
      models: T,
      params?: { [key in T[number]]?: AnyObject },
      contextArg?: ObjectPoolManifestContext,
    ) => {
      const context = contextArg ?? { type: "global" };
      const { companyId } = boundQueryClient;
      const transactions = await trpcCmVanilla.object.manifest.updates.findAll.query({
        where: { companyId },
        models,
        params,
        options: { updateOnly: true },
        context,
      });

      resolveObjectPoolTransactions(transactions);
    },
    [boundQueryClient, resolveObjectPoolTransactions],
  );

  const result = useMemo(
    () => ({
      invalidateAsync,
      updateAsync,
    }),
    [invalidateAsync, updateAsync],
  );

  return result;
}

export function useObjectPoolInvalidationForReviewModels() {
  const { invalidateAsync: baseInvalidateAsync, updateAsync: baseUpdateAsync } =
    useObjectPoolInvalidation();

  const invalidateAsync = useCallback(
    async (reviewId: number, models: Array<ObjectPoolModelName>) => {
      const context: ObjectPoolManifestContext = {
        type: "review" as const,
        entityId: reviewId,
      };

      return baseInvalidateAsync(models, context);
    },
    [baseInvalidateAsync],
  );

  const updateAsync = useCallback(
    async <T extends Array<ObjectPoolModelName>>(
      reviewId: number,
      models: T,
      params?: { [key in T[number]]?: AnyObject },
    ) => {
      // Fetch review status using vanilla trpc to avoid hooks in callbacks
      const reviewsResult = await trpcCmVanilla.object.review.findMany.query({
        where: { id: reviewId },
      });
      const review = reviewsResult?.entities?.[reviewId] as { status?: string } | undefined;

      const context: ObjectPoolManifestContext =
        review?.status === "ENDED"
          ? {
              type: "review" as const,
              entityId: reviewId,
            }
          : {
              type: "global" as const,
            };

      return baseUpdateAsync(models, params, context);
    },
    [baseUpdateAsync],
  );

  const result = useMemo(
    () => ({
      invalidateAsync,
      updateAsync,
    }),
    [invalidateAsync, updateAsync],
  );

  return result;
}

export function useObjectPoolInvalidationForRangeSetModels() {
  const { invalidateAsync: baseInvalidateAsync, updateAsync: baseUpdateAsync } =
    useObjectPoolInvalidation();

  // Get the company object to check activeRangeSetId
  const company = useCompanyObject();

  const invalidateAsync = useCallback(
    async (rangeSetId: string, models: Array<ObjectPoolModelName>) => {
      const context: ObjectPoolManifestContext =
        rangeSetId === company?.activeRangeSetId
          ? {
              type: "global" as const,
            }
          : {
              type: "rangeSet" as const,
              entityId: rangeSetId,
            };

      return baseInvalidateAsync(models, context);
    },
    [baseInvalidateAsync, company?.activeRangeSetId],
  );

  const updateAsync = useCallback(
    async <T extends Array<ObjectPoolModelName>>(
      rangeSetId: string,
      models: T,
      params?: { [key in T[number]]?: AnyObject },
    ) => {
      const context: ObjectPoolManifestContext =
        rangeSetId === company?.activeRangeSetId
          ? {
              type: "global" as const,
            }
          : {
              type: "rangeSet" as const,
              entityId: rangeSetId,
            };

      return baseUpdateAsync(models, params, context);
    },
    [baseUpdateAsync, company?.activeRangeSetId],
  );

  const result = useMemo(
    () => ({
      invalidateAsync,
      updateAsync,
    }),
    [invalidateAsync, updateAsync],
  );

  return result;
}
